import { useState } from "react";
import emailjs from "@emailjs/browser";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import "../styles/Support.css";

// EmailJS credentials are loaded from environment variables.
// These values come from the .env file in the project root.
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

function Support() {
  // ── Newsletter form state ──
  const [email, setEmail] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeUpdates, setAgreeUpdates] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrorMessage("");

    // Validate email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    // Validate Terms & Conditions
    if (!agreeTerms) {
      setErrorMessage(
        "Please agree to the Terms & Conditions to continue."
      );
      return;
    }

    // Validate updates permission
    if (!agreeUpdates) {
      setErrorMessage(
        "Please agree to receive updates to join the mailing list."
      );
      return;
    }

    setStatus("sending");

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          subscriber_email: email,
          agreed_to_updates: "yes",
        },
        EMAILJS_PUBLIC_KEY
      );

      // Successful submission
      setStatus("success");
      setEmail("");
      setAgreeTerms(false);
      setAgreeUpdates(false);
    } catch (err) {
      console.error("EmailJS error:", err);

      setStatus("error");
      setErrorMessage(
        "Something went wrong. Please try again."
      );
    }
  };

  return (
    <>
      <Navbar />

      <main className="support-page">
        <div className="support-container">

          {/* ── Header ── */}
          <div className="support-hero">

            <div className="support-hero-image-col">
              <img
                src="support-img.png"
                alt="Illustration of hands forming a heart around a laptop, blog, and chat icons"
                className="support-hero-image"
              />
            </div>

            <div className="support-hero-text-col">

              <h1 className="support-headline">
                Support the Club!
              </h1>

              <span
                className="support-headline-underline"
                aria-hidden="true"
              ></span>

              <p className="support-intro">
                If you like what you see here, I would{" "}
                <strong>LOVE IT</strong> if you consider supporting this
                blog. To support, you can subscribe to the YouTube channel,
                join the mailing list, or consider sponsoring this blog.
              </p>

              <a
                href="https://www.youtube.com/@alexisbernt"
                target="_blank"
                rel="noopener noreferrer"
                className="youtube-subscribe-link"
              >
                <svg
                  className="youtube-icon"
                  viewBox="0 0 24 24"
                  width="28"
                  height="28"
                  aria-hidden="true"
                >
                  <path
                    fill="#FF0000"
                    d="M23.498 6.186a2.994 2.994 0 0 0-2.107-2.117C19.505 3.5 12 3.5 12 3.5s-7.505 0-9.391.569A2.994 2.994 0 0 0 .502 6.186 31.36 31.36 0 0 0 0 12a31.36 31.36 0 0 0 .502 5.814 2.994 2.994 0 0 0 2.107 2.117C4.495 20.5 12 20.5 12 20.5s7.505 0 9.391-.569a2.994 2.994 0 0 0 2.107-2.117A31.36 31.36 0 0 0 24 12a31.36 31.36 0 0 0-.502-5.814z"
                  />

                  <path
                    fill="#FFFFFF"
                    d="M9.75 15.5v-7l6.25 3.5-6.25 3.5z"
                  />
                </svg>

                Subscribe on YouTube
              </a>

            </div>
          </div>

          <hr className="section-divider" />

          {/* ── Newsletter ── */}
          <section className="newsletter-section">

            <div className="newsletter-card">

              <h2 className="newsletter-heading">
                Join Cyber Club!
              </h2>

              <p className="newsletter-subtext">
                Sign up to access free materials and join the club. No spam,
                just the good stuff: Technical things explained, stories,
                trending tech news. Once a month maximum, and you can
                unsubscribe anytime.
              </p>

              <form
                className="newsletter-form"
                onSubmit={handleSubmit}
                noValidate
              >

                <input
                  type="email"
                  className="newsletter-input"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === "sending"}
                  required
                />

                <label className="newsletter-checkbox-row">

                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) =>
                      setAgreeTerms(e.target.checked)
                    }
                    disabled={status === "sending"}
                  />

                  <span>
                    By creating an account, you agree to our{" "}
                    <a href="/#/terms-conditions">
                      Terms &amp; Conditions
                    </a>{" "}
                    and{" "}
                    <a href="/#/terms-conditions">
                      Privacy Policy
                    </a>
                    .
                  </span>

                </label>

                <label className="newsletter-checkbox-row">

                  <input
                    type="checkbox"
                    checked={agreeUpdates}
                    onChange={(e) =>
                      setAgreeUpdates(e.target.checked)
                    }
                    disabled={status === "sending"}
                  />

                  <span>
                    I agree to receive updates and exclusive offers. I can
                    opt out at any time.
                  </span>

                </label>

                {errorMessage && (
                  <p className="newsletter-message newsletter-message--error">
                    {errorMessage}
                  </p>
                )}

                {status === "success" && (
                  <p className="newsletter-message newsletter-message--success">
                    You're subscribed. Check your inbox to confirm.
                  </p>
                )}

                <button
                  type="submit"
                  className="newsletter-button"
                  disabled={status === "sending"}
                >
                  {status === "sending"
                    ? "Submitting…"
                    : "Create account"}
                </button>

              </form>

            </div>

          </section>

          {/* ── Sponsors ── */}
          <section className="sponsor-section">

            <h2 className="sponsor-heading">
              Thank You to Our Sponsors!
            </h2>

            <div className="sponsor-logos">

              <img
                src="meddys-logo.png"
                alt="Meddys"
                className="sponsor-logo"
              />

            </div>

          </section>

          {/* ── Contact note ── */}
          <p className="support-contact-note">
            If you have any questions, recommendations, or thoughts, please
            feel free to reach out via the contact information in the footer.
            Thank you!
          </p>

        </div>
      </main>

      <Footer />
    </>
  );
}

export default Support;